'use client';

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';

import { CircularProgress } from '@mui/material';
import {
  InquiryFlow,
  InquiryFlowSkeleton,
  type FlowDocument,
  type ItemPickerInstance,
  type InquiryFlowSnapshot,
} from '@miso/design-system';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { MovingTypeSpecificationResponse } from '@/entities/visit-counsel/model/moving-type-intake.types';
import { MOVING_TYPE_CODE_FALLBACK_LABELS } from '@/entities/visit-counsel/model/moving-type-display';
import { movingTypeSpecToFlowDocument } from '../model/movingTypeSpecToFlowDocument';
import {
  instancesToMovingTypeIntake,
  movingTypeIntakeToInstances,
} from '../model/instancesToMovingTypeIntake';
import { markMovingTypesProgressStepCompleted } from '@/entities/visit-counsel/model/intake-helpers';
import { makeMovingTypeInstanceSummarizer } from '../model/summarizeMovingTypeInstance';

// ── Constants ────────────────────────────────────────────────────────────────

const ITEM_PICKER_QUESTION_ID = 'movingTypes';

/** 이사 유형 코드 → 토스페이스 이모지 */
const MOVING_TYPE_EMOJI: Record<string, string> = {
  DELAYED: '⏰',
  STORAGE: '📦',
  STOPOVER: '🔀',
  PARTIAL: '🧳',
  LONG_DISTANCE: '🚛',
  OFFICE: '🏢',
  SPLIT: '🏘️',
  MERGE: '🤝',
  JEJU: '✈️',
};
const ROOT_STEP_ID = 'pick';
const TERMINAL_ID = 'moving-types-saved';

// ── Flow builder ─────────────────────────────────────────────────────────────

interface BuildOptions {
  specs: MovingTypeSpecificationResponse[];
  movingDate?: string | null;
}

function buildMovingTypesFlow({ specs, movingDate }: BuildOptions): FlowDocument {
  const items = specs.map((spec) => {
    // 폴백 라벨로 사용자에게 코드가 노출되지 않게 한다.
    const displayName =
      spec.name?.trim() || MOVING_TYPE_CODE_FALLBACK_LABELS[spec.code] || spec.code;
    // spec.helpText 는 backend 디버그성 영문이 섞일 수 있어 ItemPicker item.description
    // 으로 노출하지 않는다(사용자 미승인 워딩 정책).
    return {
      id: spec.code,
      title: displayName,
      // iconDownloadUrl이 있으면 URL, 없으면 토스페이스 이모지 폴백.
      icon: spec.iconDownloadUrl ?? MOVING_TYPE_EMOJI[spec.code],
      // inline FlowDocument 로 sub-flow 주입 — flowFetcher 불필요.
      flow: movingTypeSpecToFlowDocument(spec, { movingDate }),
    };
  });

  const pickStep: FlowDocument['steps'][string] = {
    kind: 'questions',
    questions: [
      {
        id: ITEM_PICKER_QUESTION_ID,
        // 짐 기록 root flow 와 동일한 L2 레이아웃 — 누적 cascade.
        layout: 'L2',
        title: '이사 유형을 선택해주세요',
        input: {
          kind: 'item-picker',
          required: true,
          // 여러 인스턴스 허용 — 동일한 itemId 도 카드를 다시 탭해 추가 가능.
          multiple: true,
          sections: [
            {
              id: 'all',
              title: '이사 유형',
              items,
            },
          ],
          reviewTitle: '선택한 이사 유형',
          reviewButtonLabel: '이사 유형 확인',
          // 빈 상태에서도 사용자가 "네" 로 진행할 수 있도록 허용 (확인 CTA 노출).
          allowEmptyProceed: true,
          // ItemPickerRenderer 의 빈 상태 다이얼로그 워딩은 caller 가 도메인 맥락으로
          // 직접 지정해야 한다 — 미지정 시 짐 도메인용 fallback ("선택한 짐이 없어요")
          // 으로 떨어져 이사유형 화면에 짐 워딩이 노출되는 회귀가 발생한다.
          emptyTitle: '선택한 이사유형이 없어요',
          emptyDescription: '선택한 이사유형이 없어요.\n이사유형 없이 진행하시겠어요?',
        },
      },
    ],
    next: { kind: 'terminal', target: TERMINAL_ID },
  };

  return {
    schemaVersion: 1,
    id: 'moving-types-record-flow',
    initial: ROOT_STEP_ID,
    steps: { [ROOT_STEP_ID]: pickStep },
    terminals: { [TERMINAL_ID]: { kind: 'placeholder-auto' } },
  };
}

// ── Adapter ─────────────────────────────────────────────────────────────────

export interface MovingTypesFlowAdapterProps {
  visitCounselId: string;
  /**
   * 현재 counsel 스냅샷. movingTypeIntake / sessions.intake.status 를 읽어
   * 편집 prefill 과 intake-session 자동 시작 여부를 결정한다.
   */
  counsel: VisitCounselView | null | undefined;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  /** 저장 후 stage 가 'done' 으로 전환되도록 trigger — 부모는 빈 콜백이어도 무방. */
  onAfterDone: (counsel: VisitCounselView) => void;
  /** 사용자 취소(뒤로가기) */
  onCancel: () => void;
  container?: HTMLElement | null;
  /**
   * 이전 sub-stage 로부터 뒤로가기 진입 — 마지막 visible 질문에 위치.
   * 본 어댑터는 item-picker 질문 하나만 가지므로 사실상 no-op 이지만, 호출 측
   * (ProgressFlowAdapter) 의 prop 일관성을 위해 받는다.
   */
  startAtLast?: boolean;
  progressOffset?: number;
  progressTotal?: number;
  onStateChange?: (snap: InquiryFlowSnapshot) => void;
}

/**
 * 추가공간 촬영 + 모든 방 짐 기록 완료 후 진입하는 "상담 진행하기" 의 첫 step.
 *
 * Figma "상담 진행하기 플로우" 의 포장방식/이사유형 선택 화면을 item-picker 로
 * 재구성한 어댑터. 같은 이사유형을 여러 번 선택할 수 있도록 multi-instance 모드로
 * 동작하며, 각 인스턴스는 spec.attributes 기반의 sub-flow 를 통해 답변을 모은다.
 *
 * 흐름:
 *   1. specs fetch (`getMovingTypeSpecs`)
 *   2. 기존 movingTypeIntake 가 있으면 instances 로 prefill
 *   3. terminal 도달 → instancesToMovingTypeIntake → recordMovingTypeIntake 호출
 *      (intake-session 이 REQUESTED 면 startIntakeSession 자동 호출)
 *   4. refetch → onAfterDone — 페이지가 stage 재평가하여 다음 단계로 진입
 */
export function MovingTypesFlowAdapter({
  visitCounselId,
  counsel,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
  container,
  startAtLast,
  progressOffset,
  progressTotal,
  onStateChange,
}: MovingTypesFlowAdapterProps) {
  // item-picker 질문 한 개뿐이라 startAtLast 가 활성이어도 결과는 같지만
  // prop 일관성을 위해 InquiryFlow 에 그대로 전달한다.
  const startAtQuestionId = startAtLast ? ITEM_PICKER_QUESTION_ID : undefined;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLElement | null>(container ?? null);
  containerRef.current = container ?? wrapperRef.current;

  const {
    data: specs,
    isLoading: specsLoading,
    error: specsError,
  } = useSWR<MovingTypeSpecificationResponse[]>(
    visitCounselId ? ['moving-type-specs', visitCounselId] : null,
    () => myVisitCounselApi.getMovingTypeSpecs(),
    { revalidateOnFocus: false, revalidateOnMount: true }
  );

  const movingDate =
    counsel?.movingDate ?? counsel?.sessions?.intake?.movingDate ?? null;

  const flow = useMemo(() => {
    if (!specs || specs.length === 0) return null;
    return buildMovingTypesFlow({ specs, movingDate });
  }, [specs, movingDate]);

  // "선택한 이사 유형" review 시트의 instance summary — 도메인 자연어 합성.
  const summarizeInstance = useMemo(() => makeMovingTypeInstanceSummarizer(specs), [specs]);

  const initialValues = useMemo(() => {
    const restored = movingTypeIntakeToInstances(counsel?.movingTypeIntake, { specs });
    if (restored.length === 0) return undefined;
    return { [ITEM_PICKER_QUESTION_ID]: restored };
  }, [counsel?.movingTypeIntake, specs]);

  const handleComplete = useCallback(
    async (accumulated: Record<string, unknown>) => {
      const instances =
        (accumulated[ITEM_PICKER_QUESTION_ID] as ItemPickerInstance[] | undefined) ?? [];

      const payload = instancesToMovingTypeIntake(instances, { specs });

      try {
        // intake-session 이 아직 REQUESTED 면 record 전에 start 호출.
        const intakeStatus = counsel?.sessions?.intake?.status;
        if (intakeStatus === 'REQUESTED' || intakeStatus == null) {
          // null 인 경우(서버가 sessions.intake 를 미응답한 비정상 상황) 도 안전하게 시작 시도.
          try {
            await myVisitCounselApi.startIntakeSession(visitCounselId);
          } catch (err) {
            // 이미 STARTED 인데 race 로 동시에 호출된 경우 등은 record 호출에서 다시
            // 검증되므로 무시하고 진행.
            // eslint-disable-next-line no-console
            console.warn('[MovingTypesFlow] startIntakeSession failed (continuing):', err);
          }
        }

        await myVisitCounselApi.recordMovingTypeIntake(visitCounselId, payload);
        markMovingTypesProgressStepCompleted(visitCounselId);
        showToast(
          instances.length === 0 ? '이사 유형 없이 저장되었습니다.' : '이사 유형이 저장되었습니다.',
          'success'
        );

        const result = await refetch();
        const updated = result?.counsel ?? null;
        if (updated) onAfterDone(updated);
        else onCancel();
      } catch (err) {
        showToast(err instanceof Error ? err.message : '이사 유형 저장에 실패했습니다.', 'error');
      }
    },
    [
      counsel?.sessions?.intake?.status,
      visitCounselId,
      showToast,
      refetch,
      onAfterDone,
      onCancel,
      specs,
    ]
  );

  const handleError = useCallback(
    (err: Error) => {
      showToast(err.message ?? '플로우 오류가 발생했습니다.', 'error');
      onCancel();
    },
    [showToast, onCancel]
  );

  useEffect(() => {
    if (specsError) {
      showToast(
        specsError instanceof Error ? specsError.message : '이사 유형 스펙 조회 실패',
        'error'
      );
    }
  }, [specsError, showToast]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      {specsLoading || !flow ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {specsLoading ? <CircularProgress /> : <InquiryFlowSkeleton />}
        </div>
      ) : (
        <InquiryFlow
          flow={flow}
          initialValues={initialValues}
          registries={{ terminals: {} }}
          onComplete={handleComplete}
          onCancel={onCancel}
          onError={handleError}
          container={containerRef.current}
          startAtQuestionId={startAtQuestionId}
          progressOffset={progressOffset}
          progressTotal={progressTotal}
          onStateChange={onStateChange}
          resolveInstanceSummary={summarizeInstance}
        />
      )}
    </div>
  );
}
